import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { X, Camera, CheckCircle, AlertCircle } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { uploadVideo } from '@/lib/uploadVideo';
import { draftStore } from '@/lib/draftStore';

const log = createLogger('POST');

interface CreatePostModalProps {
  visible: boolean;
  onClose: () => void;
  onPostCreated: () => void;
  videoUri?: string | null;
  postDraftId?: string;
  initialContent?: string;
}

export default function CreatePostModal({
  visible,
  onClose,
  onPostCreated,
  videoUri: externalVideoUri,
  postDraftId,
  initialContent,
}: CreatePostModalProps) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const [content, setContent] = useState('');
  const [mediaUri, setMediaUri] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'video' | 'image' | null>(null);
  const [loading, setLoading] = useState(false);
  const [draftId] = useState(() => `post-draft-${Date.now()}`);
  const [isVerificationPost, setIsVerificationPost] = useState(false);
  const [showVerificationConfirm, setShowVerificationConfirm] = useState(false);
  const [hasSeenVerificationWarning, setHasSeenVerificationWarning] = useState(false);

  useEffect(() => {
    if (externalVideoUri && postDraftId === draftId) {
      setMediaUri(externalVideoUri);
      setMediaType('video');
    }
  }, [externalVideoUri, postDraftId, draftId]);

  useEffect(() => {
    if (initialContent) {
      setContent(initialContent);
    }
  }, [initialContent]);

  const recordVideo = () => {
    draftStore.setFeedDraft(draftId, {
      content,
      mediaUri,
      mediaType,
    });

    router.push({
      pathname: '/record-video',
      params: {
        source: 'feed',
        contextId: draftId,
      },
    });
  };

  const handleVerificationToggle = () => {
    if (!isVerificationPost && !hasSeenVerificationWarning) {
      setShowVerificationConfirm(true);
    } else {
      setIsVerificationPost(!isVerificationPost);
    }
  };

  const handleVerificationConfirm = () => {
    setIsVerificationPost(true);
    setHasSeenVerificationWarning(true);
    setShowVerificationConfirm(false);
  };

  const handleVerificationCancel = () => {
    setIsVerificationPost(false);
    setShowVerificationConfirm(false);
  };

  const uploadMedia = async (uri: string, type: 'video' | 'image'): Promise<string> => {
    if (type === 'video') {
      return await uploadVideo(uri, profile!.id);
    }

    const fileExt = uri.split('.').pop();
    const fileName = `${profile!.id}/${Date.now()}.${fileExt}`;

    const response = await fetch(uri);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    const { data, error } = await supabase.storage
      .from('workout-media')
      .upload(fileName, arrayBuffer, {
        contentType: 'image/jpeg',
        upsert: false,
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('workout-media')
      .getPublicUrl(data.path);

    return publicUrl;
  };

  const handleSubmit = async () => {
    if (!content.trim() && !mediaUri) {
      Alert.alert('Missing Content', "Add some text or media to your post.");
      return;
    }

    if (!profile?.id) {
      Alert.alert('Not Signed In', "Sign in to create a post.");
      return;
    }

    setLoading(true);

    try {
      let uploadedMediaUrl: string | null = null;

      if (mediaUri && mediaType) {
        log.info('Uploading media...', { mediaType, uri: mediaUri });
        try {
          uploadedMediaUrl = await uploadMedia(mediaUri, mediaType);
          log.info('Media uploaded successfully:', uploadedMediaUrl);
        } catch (uploadError: any) {
          log.error('Media upload failed:', uploadError);
          throw new Error(`Media upload failed: ${uploadError.message}`);
        }
      }

      log.info('Creating post...', {
        user_id: profile.id,
        caption: content.trim() || null,
        media_url: uploadedMediaUrl,
        media_type: mediaType,
        gym_id: profile?.home_gym_id,
        visibility: 'public',
      });

      const { data, error } = await supabase.from('posts').insert({
        user_id: profile.id,
        caption: content.trim() || null,
        post_type: 'general',
        media_url: uploadedMediaUrl,
        media_type: mediaType,
        gym_id: profile?.home_gym_id,
        visibility: 'public',
        is_competition: false,
        is_verification_post: mediaType === 'video' ? isVerificationPost : false,
      }).select().single();

      if (error) {
        log.error('Post creation failed:', error);
        throw new Error(`Failed to create post: ${error.message}`);
      }

      log.info('Post created successfully:', data);

      setContent('');
      setMediaUri(null);
      setMediaType(null);
      setIsVerificationPost(false);

      onPostCreated();
      onClose();

      if (Platform.OS === 'web') {
        alert('Post Created');
      } else {
        Alert.alert('Posted', 'Post Created');
      }
    } catch (error: any) {
      log.error('Post submission error:', error);

      if (Platform.OS === 'web') {
        alert("Couldn't create your post. Please try again.");
      } else {
        Alert.alert('Oops', "Couldn't create your post. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <TouchableOpacity onPress={onClose}>
            <X size={24} color={theme.colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
            Create Post
          </Text>
          <TouchableOpacity
            onPress={handleSubmit}
            disabled={loading || (!content.trim() && !mediaUri)}>
            <Text
              style={[
                styles.postButton,
                {
                  color:
                    !content.trim() && !mediaUri
                      ? theme.colors.textTertiary
                      : theme.colors.accent,
                },
              ]}>
              {loading ? 'Posting...' : 'Post'}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <View style={styles.profileRow}>
            <View style={[styles.avatar, { backgroundColor: theme.colors.accent }]}>
              <Text style={[styles.avatarText, { color: theme.colors.accentForeground }]}>
                {profile?.display_name?.charAt(0).toUpperCase()}
              </Text>
            </View>
            <View>
              <Text style={[styles.displayName, { color: theme.colors.text }]}>
                {profile?.display_name}
              </Text>
              <Text style={[styles.username, { color: theme.colors.textSecondary }]}>
                @{profile?.handle}
              </Text>
            </View>
          </View>

          <TextInput
            style={[
              styles.input,
              {
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={content}
            onChangeText={setContent}
            placeholder="What's happening at the gym?"
            placeholderTextColor={theme.colors.textTertiary}
            multiline
            numberOfLines={6}
            autoFocus
          />

          {mediaUri && (
            <View
              style={[
                styles.mediaPreview,
                {
                  backgroundColor: theme.colors.surfaceVariant,
                  borderColor: theme.colors.border,
                },
              ]}>
              <Text style={[styles.mediaText, { color: theme.colors.text }]}>
                {mediaType === 'video' ? 'Video' : 'Image'} selected
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setMediaUri(null);
                  setMediaType(null);
                  setIsVerificationPost(false);
                }}>
                <X size={20} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
          )}

          {mediaType === 'video' && (
            <TouchableOpacity
              style={[
                styles.verificationToggle,
                {
                  backgroundColor: isVerificationPost
                    ? theme.colors.accentLight
                    : theme.colors.surfaceVariant,
                  borderColor: isVerificationPost
                    ? theme.colors.accent
                    : theme.colors.border,
                },
              ]}
              onPress={handleVerificationToggle}>
              <View style={styles.verificationToggleContent}>
                <CheckCircle
                  size={20}
                  color={isVerificationPost ? theme.colors.accent : theme.colors.textSecondary}
                />
                <View style={styles.verificationToggleText}>
                  <Text
                    style={[
                      styles.verificationToggleTitle,
                      { color: theme.colors.text },
                    ]}>
                    Leaderboard Verification
                  </Text>
                  <Text
                    style={[
                      styles.verificationToggleHelper,
                      { color: theme.colors.textSecondary },
                    ]}>
                    Enables community voting. Only verification posts affect Callus Score.
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          )}

          <View style={styles.mediaButtons}>
            <TouchableOpacity
              style={[
                styles.mediaButton,
                {
                  backgroundColor: theme.colors.surfaceVariant,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={recordVideo}>
              <Camera size={24} color={theme.colors.accent} />
              <Text style={[styles.mediaButtonText, { color: theme.colors.text }]}>
                Record Video
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <Modal
        visible={showVerificationConfirm}
        transparent
        animationType="fade"
        onRequestClose={handleVerificationCancel}>
        <View style={[styles.confirmOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.8)' }]}>
          <View style={[styles.confirmModal, { backgroundColor: theme.colors.surface, shadowColor: theme.colors.background, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8 }]}>
            <View style={styles.confirmHeader}>
              <AlertCircle size={32} color={theme.colors.accent} />
              <Text style={[styles.confirmTitle, { color: theme.colors.text }]}>
                Enable Leaderboard Verification?
              </Text>
            </View>
            <Text style={[styles.confirmBody, { color: theme.colors.textSecondary }]}>
              This will allow the community to judge your lift.{'\n'}
              They&apos;ll vote on whether it was a valid lift.{'\n'}
              Votes affect your Callus Score and leaderboard eligibility.
            </Text>
            <View style={styles.confirmButtons}>
              <TouchableOpacity
                style={[
                  styles.confirmButton,
                  styles.confirmButtonSecondary,
                  { borderColor: theme.colors.border },
                ]}
                onPress={handleVerificationCancel}>
                <Text style={[styles.confirmButtonText, { color: theme.colors.textSecondary }]}>
                  Cancel
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.confirmButton,
                  styles.confirmButtonPrimary,
                  { backgroundColor: theme.colors.accent },
                ]}
                onPress={handleVerificationConfirm}>
                <Text style={[styles.confirmButtonText, { color: theme.colors.accentForeground }]}>
                  Enable
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    paddingTop: Platform.OS === 'ios' ? 50 : 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  postButton: {
    fontSize: 16,
    fontWeight: '700',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '700',
  },
  displayName: {
    fontSize: 16,
    fontWeight: '700',
  },
  username: {
    fontSize: 14,
  },
  input: {
    fontSize: 16,
    lineHeight: 24,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 0,
    minHeight: 120,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  mediaPreview: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderWidth: 1,
    borderRadius: 0,
    marginBottom: 16,
  },
  mediaText: {
    fontSize: 14,
    fontWeight: '600',
  },
  mediaButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  mediaButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderWidth: 1,
    borderRadius: 0,
  },
  mediaButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  verificationToggle: {
    padding: 16,
    borderWidth: 1,
    borderRadius: 0,
    marginBottom: 16,
  },
  verificationToggleContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  verificationToggleText: {
    flex: 1,
  },
  verificationToggleTitle: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 4,
  },
  verificationToggleHelper: {
    fontSize: 13,
    lineHeight: 18,
  },
  confirmOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 2,
    padding: 24,
    elevation: 8,
  },
  confirmHeader: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  confirmTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  confirmBody: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 24,
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmButtonSecondary: {
    borderWidth: 1,
  },
  confirmButtonPrimary: {
    borderWidth: 0,
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});
